import React from 'react';
import { DateTime } from 'luxon';
import {
  getValueBgClass,
  getCellStatusClassRules,
  formatUpsideDownside,
  getCellStatusBackgroundByActionCompany,
  formatPriceChange,
  formatRatingsChange,
  formatLongPrice,
} from '../lib/utils';
import { numberShorthand } from '@benzinga/utils';
import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { Ratings } from '@benzinga/calendar-manager';
import { FaArrowRightLong, FaArrowLeftLong, FaCaretUp, FaCaretDown } from 'react-icons/fa6';
import { Progress, Slider } from 'antd';
import { priceFilterPresets } from './ColumnsDef';

const defaultFilterParams = {
  newRowsAction: 'keep',
};

const RatingChangeCell = ({ data }: { data: Ratings }) => {
  return (
    <div className={`w-full flex flex-row gap-1 items-center justify-between hover:bg-transparent`}>
      <div className="flex flex-col items-start justify-between flex-1 max-w-[120px]">
        <span className="sub-text text-2xs">Previous</span>
        <div className="text-sm font-bold text-[#283d59] uppercase truncate max-w-[120px]">
          {data.action_company === 'Initiates Coverage On' ? 'Initiates' : data.rating_prior}
        </div>
      </div>
      <div className="flex items-center justify-center">
        <div className="ratings-change-icon">
          <FaArrowRightLong className=" text-xs" />
        </div>
      </div>
      <div className="flex flex-col items-end justify-between flex-1 max-w-[120px]">
        <span className="sub-text text-2xs">Current</span>
        <div className="text-sm font-bold text-[#151c26] uppercase truncate max-w-[120px]">{data.rating_current}</div>
      </div>
    </div>
  );
};

const PriceRatingChangeCell = ({ data }: { data: Ratings }) => {
  if (!data.pt_current) return null;
  const from = Number(data.pt_prior ?? 0);
  const to = Number(data.pt_current);
  const current = Number(data?.quote?.last ?? data?.quote?.lastTradePrice);
  const isIncrease = to > from; // Target increase vs decrease

  // Create a price range for the visualization
  const minPrice = Math.min(current, from, to) * 0.9;
  const maxPrice = Math.max(current, from, to) * 1.1;
  const priceRange = maxPrice - minPrice;

  // Calculate positions as percentages (0-100)
  const currentPosition = ((current - minPrice) / priceRange) * 100;
  const fromPosition = ((from - minPrice) / priceRange) * 100;
  const toPosition = ((to - minPrice) / priceRange) * 100;

  return (
    <div
      className={`price-rating-change-cell w-full ${isIncrease ? 'price-increase' : from !== to ? 'price-decrease' : ''}`}
    >
      <div className="w-full min-w-[100px] relative">
        <div className="flex items-center justify-between gap-2 w-full z-[2]">
          <span
            className={`text-[#283d59] font-bold ${!isIncrease && from !== to ? 'font-extrabold family-sans text-red-700' : ''}`}
          >
            {Math.min(from, to) !== 0 ? numberShorthand(Math.min(from, to), 2, true) : null}
          </span>
          {!isIncrease && from !== to ? (
            <div className="ratings-change-icon">
              <FaArrowLeftLong className="text-xs" />
            </div>
          ) : (
            <div className="ratings-change-icon">
              <FaArrowRightLong className=" text-xs" />
            </div>
          )}
          <span className={`text-[#283d59] font-bold ${isIncrease ? 'font-extrabold family-sans text-green-700' : ''}`}>
            {numberShorthand(Math.max(from, to), 2, true)}
          </span>
        </div>
        <div className="w-full h-2 mt-0.5 relative">
          <Slider
            defaultValue={[fromPosition === 0 ? toPosition : fromPosition, toPosition]}
            disabled
            max={100}
            min={0}
            range
            tooltip={{ formatter: null }}
          />
          <div
            className="absolute -bottom-[0.3rem] w-1 h-[14px] bg-bzblue-900 rounded-full border border-white"
            style={{ left: `${currentPosition}%`, zIndex: 1 }}
            title={`Current: ${current}`}
          />
        </div>
      </div>
    </div>
  );
};

const DetailedTickerCell = ({ data }: { data: Ratings & { logo: string } }) => {
  return (
    <a className="w-full group" href={`/quote/${data.ticker}`} rel="noopener noreferrer" target="_blank">
      <div className="company-name-cell">
        <div className="flex flex-col truncate">
          <span className="text-base font-bold text-[#283d59] truncate group-hover:text-bzblue-700">{data.ticker}</span>
          <span className="sub-text truncate">{data.name}</span>
        </div>
      </div>
    </a>
  );
};

const UpsideDownsideCell = ({ value }: { value: number | string }) => {
  const parsedValue = typeof value === 'number' ? value : Number((value as string)?.replace('%', '') ?? 0);
  return (
    <div className="w-full min-w-[80px]">
      <div
        className={`h-[10px] mt-2 flex items-center gap-1 justify-between ${parsedValue > 0 ? 'text-green-500' : 'text-red-500'}`}
      >
        {parsedValue > 0 ? <FaCaretUp className="text-xs" /> : <FaCaretDown className="text-xs" />}
        <span className="main-text">{value ? `${parsedValue}%` : '—'}</span>
      </div>
      <Progress
        percent={Math.abs(parsedValue)}
        showInfo={false}
        size="small"
        strokeColor={parsedValue > 0 ? '#30BF60' : '#FF4050'}
        style={parsedValue < 0 ? { transform: 'scaleX(-1)' } : {}}
      />
    </div>
  );
};

const AnalystCell = ({ data }: { data: Ratings }) => {
  const smartScore = Number(data?.ratings_accuracy?.smart_score) || null;
  const analystName: string = data?.analyst_name;
  const analystId: string = data?.analyst_id;

  if (!analystName) return '—';

  const getBubbleClassName = (smartScore: number) => {
    if (smartScore >= 50) return 'green';
    else return 'red';
  };

  const convertToSlug = (value: string) => value.toLowerCase()?.replace(' ', '-');
  const encodedAnalystName = encodeURIComponent(convertToSlug(analystName));
  const href = `/analyst-stock-ratings/analyst/${analystId}/${encodedAnalystName}`;
  const firm = data?.analyst;

  return (
    <a className="w-full min-w-[160px]" href={href}>
      <div className="w-full flex flex-row items-center justify-between group">
        <div className="flex flex-col truncate">
          <span className="text-sm text-[#283d59] truncate group-hover:text-bzblue-700">{analystName}</span>
          <span className="sub-text truncate">{firm}</span>
        </div>
        {typeof smartScore === 'number' && (
          <div className="flex flex-col items-end max-h-[36px]">
            <span className={`bz-ag-table__analyst-smart-score ${getBubbleClassName(smartScore)}`}>
              {Math.floor(smartScore) ?? '-'}%
            </span>
            <Progress
              percent={smartScore}
              showInfo={false}
              size="small"
              steps={10}
              strokeColor={getBubbleClassName(smartScore)}
            />
          </div>
        )}
      </div>
    </a>
  );
};

const colDef: (ColDef | ColGroupDef)[] = [
  {
    cellClass: 'date-cell',
    field: 'date',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
    },
    headerName: 'Date',
    initialWidth: 100,
    valueFormatter: ({ value }: { value: string }) => {
      return DateTime.fromISO(value).toFormat('MMM d, yyyy');
    },
    width: 100,
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: ({ data }: { data: Ratings & { logo: string } }) => <DetailedTickerCell data={data} />,
    cellRendererParams: {
      hideOnInitialTicker: true,
    },
    field: 'name',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Company',
    initialWidth: 190,
  },
  {
    cellRendererParams: {
      hideOnInitialTicker: true,
    },
    cellStyle: { fontSize: '14px', fontWeight: 'bold', textAlign: 'left' },
    field: 'current_price',
    filter: 'priceFilter',
    headerName: 'Current Price',
    initialWidth: 120,
    valueFormatter: ({ data }: { data: Ratings }) => formatLongPrice(Number(data?.quote?.lastTradePrice)),
    valueGetter: ({ data }: { data: Ratings }) =>
      data?.quote?.lastTradePrice ? Number(data.quote.lastTradePrice) : null,
  },
  {
    cellClass: ({ value }: { value: number | string }) => getValueBgClass(value) + ' gated-cell',
    cellRenderer: ({ value }: { value: number | string }) => <UpsideDownsideCell value={value} />,
    cellStyle: { fontWeight: 'bold', textAlign: 'left' },
    field: 'upside_downside',
    filter: 'percentFilter',
    headerName: 'Upside/Downside',
    initialWidth: 120,
    tooltipValueGetter: params => {
      return `Price target: ${formatLongPrice(params?.data?.pt_current)}`;
    },
    valueFormatter: ({ value }: { value: number }) => (value ? `${value}%` : '—'),
    valueGetter: ({ data }: { data: Ratings }) =>
      formatUpsideDownside(Number(data?.quote?.last ?? data?.quote?.lastTradePrice), data.pt_current),
  },
  {
    cellClass: 'gated-cell',
    cellRenderer: ({ data }: { data: Ratings }) => <AnalystCell data={data} />,
    cellRendererParams: {
      hideOnInitialTicker: true,
    },
    cellStyle: { cursor: 'pointer' },
    comparator: (_valueA, _valueB, nodeA, nodeB) => {
      const smartScoreA = Number(nodeA.data?.ratings_accuracy?.smart_score ?? 0);
      const smartScoreB = Number(nodeB.data?.ratings_accuracy?.smart_score ?? 0);
      if (smartScoreA === smartScoreB) return 0;
      else if (smartScoreA > smartScoreB) return 1;
      return -1;
    },
    field: 'analyst_name',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Analyst & Accuracy',
    initialWidth: 220,
    tooltipValueGetter: params => {
      const analystName = params?.data?.analyst_name;
      const smartScore = params?.data?.ratings_accuracy?.smart_score;
      return analystName && !isNaN(smartScore)
        ? `${analystName} is correct ${Math.floor(smartScore) ?? '-'}% of the time`
        : '';
    },
  },
  {
    cellClass: ({ data }: { data: Ratings }) => getCellStatusClassRules(data.action_pt) + ' gated-cell',
    cellRenderer: ({ data }: { data: Ratings }) => <PriceRatingChangeCell data={data} />,
    field: 'pt_prior',
    filter: 'priceFilter',
    filterParams: {
      suppressSorting: false,
      values: priceFilterPresets,
      ...defaultFilterParams,
    },
    headerName: 'Price Target Change',
    initialWidth: 150,
    tooltipValueGetter: params => {
      if (!params?.data?.pt_current) return '';
      if (params?.data?.action_company === 'Initiates Coverage On' || params?.data?.pt_prior === 0)
        return `The current price target is ${formatLongPrice(params?.data?.pt_current)}.`;
      const change = params?.data?.pt_current - params?.data?.pt_prior;
      let tooltipString = `The price target ${change > 0 ? 'has increased to ' : change < 0 ? 'has decreased to ' : 'is '}
      ${formatLongPrice(params?.data?.pt_current)}.`;
      const isPriceIncreasing = Number(params?.data?.quote?.lastTradePrice) < Number(params?.data?.pt_current);
      if (isPriceIncreasing !== undefined) {
        tooltipString += ` The current price is predicted to ${isPriceIncreasing ? 'increase.' : 'decrease.'}`;
      }
      return tooltipString;
    },
    valueFormatter: ({ data }: { data: Ratings }) => formatPriceChange(data.pt_prior, data.pt_current),
    valueGetter: ({ data }: { data: Ratings }) => (data.pt_current ? Number(data.pt_current) : null),
  },
  {
    cellClass: ({ data }: { data: Ratings }) =>
      getCellStatusBackgroundByActionCompany(data?.action_company ?? '') + ' gated-cell',
    cellRenderer: ({ data }: { data: Ratings }) => <RatingChangeCell data={data} />,
    cellStyle: { fontWeight: 'bold', textAlign: 'left' },
    field: 'rating_current',
    filter: 'agSetColumnFilter',
    filterParams: {
      suppressSorting: true,
      ...defaultFilterParams,
    },
    headerName: 'Previous / Current Rating',
    initialWidth: 300,
    tooltipValueGetter: params => {
      if (params?.data?.action_company === 'Maintains')
        return `The rating maintains at ${params?.data?.rating_current.toUpperCase()}.`;
      if (params?.data?.action_company === 'Upgrades')
        return `The rating has been upgraded to ${params?.data?.rating_current.toUpperCase()}.`;
      if (params?.data?.action_company === 'Downgrades')
        return `The rating has been downgraded to ${params?.data?.rating_current.toUpperCase()}.`;
      if (params?.data?.action_company === 'Reiterates')
        return `The rating has been reiterated to ${params?.data?.rating_current.toUpperCase()}.`;
      if (params?.data?.action_company === 'Initiates Coverage On')
        return `The analyst has initiated coverage. The current rating is ${params?.data?.rating_current.toUpperCase()}.`;
      if (params?.data?.action_company === 'Terminates Coverage On') return `The analyst has terminated coverage.`;
      if (params?.data?.action_company === 'Removes') return `The analyst has removed the rating.`;
      if (params?.data?.action_company === 'Suspends') return `The analyst has suspended the rating.`;
      if (params?.data?.action_company === 'Firm Dissolved') return `The analyst's firm has been dissolved.`;
      return params?.data?.action_company;
    },
    valueFormatter: ({ data }: { data: Ratings }) =>
      formatRatingsChange(data.rating_prior, data.rating_current, data.action_company ?? ''),
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: () => {
      return (
        // eslint-disable-next-line react/jsx-no-target-blank
        <a href="https://www.benzinga.com/profile/portfolio/?action=login&utm_source=calendars" target="_blank">
          Get Alert
        </a>
      );
    },
    field: 'get_alert',
    filter: false,
    headerName: 'Get Alert',
    initialWidth: 70,
    suppressAutoSize: true,
    suppressHeaderMenuButton: true,
    suppressSizeToFit: true,
  },
];

export default colDef;
